import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ExpandMore } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

type Props = {
  onDelete: () => void;
  onDeactivate: () => void;
};

const lexendFont = { fontFamily: "'Lexend', sans-serif" };

const DangerZone: React.FC<Props> = ({ onDelete, onDeactivate }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [openRow, setOpenRow] = useState(false); // still available if you want to toggle bottom UI

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded);
  };

  return (
    <Box sx={{ ...lexendFont }}>
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={{
          boxShadow: "none",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 1,
          backgroundColor: COLORS.panel,
          "&:before": {
            display: "none",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: COLORS.text }} />}
          sx={{
            "& .MuiAccordionSummary-content": {
              margin: "12px 0",
            },
          }}
        >
          <Stack direction="column" spacing={1}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: COLORS.red }}>
              Danger Zone
            </Typography>
            <Typography variant="subtitle2" fontWeight={400} sx={{ color: COLORS.muted }}>
              Irreversible actions that will affect your account.
            </Typography>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ backgroundColor: COLORS.panel }}>
          <Stack spacing={2}>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setDeactivateDialogOpen(true)}
              sx={{
                borderColor: "#F59E0B",
                color: "#F59E0B",
                "&:hover": {
                  borderColor: "#D97706",
                  backgroundColor: "rgba(245,158,11,0.1)",
                },
              }}
            >
              Deactivate Account
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              sx={{
                backgroundColor: COLORS.red,
                color: COLORS.text,
                "&:hover": {
                  backgroundColor: "#b20710",
                },
              }}
            >
              Delete Account
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Optional Row Toggle */}
      {/* 
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 2,
          boxShadow: 0.5,
          "&:hover": { boxShadow: 2 },
          cursor: "pointer",
        }}
        onClick={() => setOpenRow((prev) => !prev)}
      >
        <Stack spacing={0}>
          <Typography fontWeight={600}>Deactivation and deletion</Typography>
          <Typography variant="body2" color="text.secondary">
            Temporarily deactivate or permanently delete your account
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          sx={{
            ...lexendFont,
            minWidth: 36,
            width: 36,
            height: 36,
            borderRadius: "50%",
            padding: 0,
            background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
            "&:hover": {
              opacity: 0.9,
              background: "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)",
            },
          }}
        >
          {openRow ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Button>
      </Stack>
      */}

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            color: COLORS.text,
          },
        }}
      >
        <DialogTitle sx={{ ...lexendFont, color: COLORS.text }}>
          Delete Account?
        </DialogTitle>
        <DialogContent sx={{ color: COLORS.muted }}>
          This action is irreversible. All your data will be permanently removed.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ ...lexendFont, color: COLORS.muted }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDelete();
              setDeleteDialogOpen(false);
              enqueueSnackbar("Account deleted successfully.", {
                variant: "success",
              });
            }}
            color="error"
            variant="contained"
            sx={{
              ...lexendFont,
              textTransform: "none",
              backgroundColor: COLORS.red,
              color: COLORS.text,
              "&:hover": {
                backgroundColor: "#b20710",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Dialog */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => setDeactivateDialogOpen(false)}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            color: COLORS.text,
          },
        }}
      >
        <DialogTitle sx={{ ...lexendFont, color: COLORS.text }}>
          Deactivate Account?
        </DialogTitle>
        <DialogContent sx={{ color: COLORS.muted }}>
          You can reactivate later by logging in.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeactivateDialogOpen(false)}
            sx={{ ...lexendFont, color: COLORS.muted }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDeactivate();
              setDeactivateDialogOpen(false);
              enqueueSnackbar("Account deactivated successfully.", {
                variant: "success",
              });
            }}
            color="warning"
            variant="outlined"
            sx={{
              ...lexendFont,
              textTransform: "none",
              borderColor: "#F59E0B",
              color: "#F59E0B",
              "&:hover": {
                borderColor: "#D97706",
                backgroundColor: "rgba(245,158,11,0.1)",
              },
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DangerZone;
